/**
 * API Client — fetches widget config and LiveKit token
 * All calls go through the same-origin Fastify server (no external URLs exposed)
 */
import { logger } from './logger.js';

export class ApiClient {
    /**
     * @param {Object} options
     * @param {string} options.appId
     * @param {string} [options.configUrl] — Base URL of customers.new (Fastify)
     */
    constructor(options = {}) {
        this.appId = options.appId;
        this.configUrl = (options.configUrl || options.server || options.serverUrl || (typeof document !== 'undefined' ? (document.querySelector('script[data-server]')?.getAttribute('data-server') || '') : '')).replace(/\/$/, '');
        // Base headers for all requests — includes ngrok bypass for dev/tunnel usage
        this._headers = {
            'Content-Type': 'application/json',
            'ngrok-skip-browser-warning': 'true',
            'X-Widget-Version': typeof __IGREET_WIDGET_VERSION__ !== 'undefined' ? __IGREET_WIDGET_VERSION__ : 'dev',
        };
    }

    /**
     * Identify the consumer via fingerprint matching.
     * POST /widget/identify — returns a stable visitorId for this device.
     * @param {Object} params
     * @param {Object} params.rawJsFp — Raw fingerprint signals from collectFingerprint()
     * @param {Object} params.cookieSnapshot — Cookie snapshot from buildCookieSnapshot()
     * @returns {Promise<{visitorId, isNew, confidence, matchedSignals, visitCount}>}
     */
    async identify({ rawJsFp, cookieSnapshot }) {
        const url = `${this.configUrl}/widget/identify`;
        const res = await fetch(url, {
            method: 'POST',
            headers: this._headers,
            body: JSON.stringify({
                appId: this.appId,
                rawJsFp,
                cookieSnapshot,
            }),
        });
        if (!res.ok) {
            throw new Error(`[iGreet] Failed to identify: ${res.status}`);
        }
        return res.json();
    }

    /**
     * Fetch widget configuration from customers.new
     * GET /widget/config/:appId
     */
    async fetchConfig() {
        const url = `${this.configUrl}/widget/config/${this.appId}`;
        const res = await fetch(url, { headers: this._headers });
        if (!res.ok) {
            throw new Error(`[iGreet] Failed to fetch widget config: ${res.status}`);
        }
        const data = await res.json();
        return data.config;
    }

    /**
     * Fetch LiveKit token via server-side proxy
     * POST /widget/token — server looks up pipelineAppId and proxies to token service
     * @param {Object} params
     * @param {string} params.identity
     * @param {string} params.roomName
     */
    async fetchToken({ identity, roomName }) {
        const url = `${this.configUrl}/widget/token`;
        const res = await fetch(url, {
            method: 'POST',
            headers: this._headers,
            body: JSON.stringify({
                appId: this.appId,
                identity,
                roomName,
            }),
        });
        if (!res.ok) {
            throw new Error(`[iGreet] Failed to fetch token: ${res.status}`);
        }
        return res.json();
    }

    /**
     * Persist an interaction (message) to the inbox backend.
     * POST /widget/interaction — fire-and-forget, does not block chat flow.
     * @param {Object} params
     * @param {string} params.visitorId
     * @param {string} [params.visitorName]
     * @param {string} params.role — 'consumer' or 'bot'
     * @param {string} params.content
     * @param {string} [params.type] — 'text' | 'voice' | 'system'
     */
    async persistInteraction({ visitorId, visitorName, role, content, type = 'text' }) {
        const url = `${this.configUrl}/widget/interaction`;
        try {
            await fetch(url, {
                method: 'POST',
                headers: this._headers,
                body: JSON.stringify({
                    appId: this.appId,
                    visitorId,
                    visitorName,
                    role,
                    content,
                    type,
                }),
            });
        } catch (err) {
            // Fire-and-forget: log but don't disrupt the user experience
            logger.warn('[iGreet] Failed to persist interaction:', err.message);
        }
    }

    /**
     * Notify the server of session status changes (connect/disconnect/mode switch).
     * Triggers SSE event so the messaging page updates takeover buttons in real-time.
     * POST /widget/session-status — fire-and-forget
     * @param {Object} params
     * @param {string} params.visitorId
     * @param {string} params.status — 'active' | 'ended'
     * @param {string} params.channel — 'chat' | 'voice'
     * @param {string} [params.roomName] — LiveKit room name
     */
    async notifySessionStatus({ visitorId, status, channel = 'chat', roomName = '' }) {
        const url = `${this.configUrl}/widget/session-status`;
        try {
            await fetch(url, {
                method: 'POST',
                headers: this._headers,
                body: JSON.stringify({
                    appId: this.appId,
                    visitorId,
                    status,
                    channel,
                    roomName,
                }),
            });
        } catch (err) {
            // Fire-and-forget: log but don't disrupt the user experience
            logger.warn('[iGreet] Failed to notify session status:', err.message);
        }
    }

    /**
     * Notify the server that the consumer is typing.
     * Triggers SSE event so the messaging page shows "is typing..." indicator.
     * POST /widget/typing — fire-and-forget, debounce on caller side
     * @param {Object} params
     * @param {string} params.visitorId
     */
    async notifyTyping({ visitorId }) {
        const url = `${this.configUrl}/widget/typing`;
        try {
            await fetch(url, {
                method: 'POST',
                headers: this._headers,
                body: JSON.stringify({
                    appId: this.appId,
                    visitorId,
                }),
            });
        } catch (err) {
            // Fire-and-forget
        }
    }

    /**
     * Fire-and-forget session "ended" signal using sendBeacon.
     * This is the ONLY reliable way to notify the server during page unload
     * (refresh, tab close, navigation away). Regular fetch() gets cancelled.
     * @param {Object} params
     * @param {string} params.visitorId
     * @param {string} params.channel
     * @param {string} params.roomName
     */
    sendSessionEndedBeacon({ visitorId, channel = 'chat', roomName = '' }) {
        if (!navigator.sendBeacon) {
            // Fallback for very old browsers (unlikely)
            this.notifySessionStatus({ visitorId, status: 'ended', channel, roomName });
            return;
        }
        const url = `${this.configUrl}/widget/session-status`;
        const blob = new Blob(
            [JSON.stringify({
                appId: this.appId,
                visitorId,
                status: 'ended',
                channel,
                roomName,
            })],
            { type: 'application/json' }
        );
        navigator.sendBeacon(url, blob);
    }

    /**
     * Resolve image metadata and streaming URL by filename or ID
     * GET /widget/config/:appId/resolve-image?filename=...
     * @param {string} filename
     * @returns {Promise<{id, filename, title, description, tags, url}>}
     */
    async resolveImage(filename) {
        const url = `${this.configUrl}/widget/config/${this.appId}/resolve-image?filename=${encodeURIComponent(filename)}`;
        const res = await fetch(url, { headers: this._headers });
        if (!res.ok) {
            throw new Error(`[iGreet] Image resolve failed: ${res.status}`);
        }
        const data = await res.json();
        // Prepend configUrl to relative url if needed
        if (data.url && data.url.startsWith('/') && this.configUrl) {
            data.url = `${this.configUrl}${data.url}`;
        }
        return data;
    }
}

